import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Truck } from "lucide-react";
import { formatDOP } from "@/lib/format";

/**
 * DeliveryNoteBuilder — modal para crear un conduce.
 *
 * Reglas del negocio:
 *  - Siempre asociado a una factura. No se pueden editar/agregar/quitar items;
 *    éstos se copian desde la factura seleccionada.
 *  - Si se elige un cliente, la lista de facturas se filtra por ese cliente.
 *    Si no se elige cliente y se elige factura, el cliente se auto-completa.
 *  - Cliente y factura pueden cambiarse o limpiarse en cualquier momento.
 *  - Chofer y Vehículo son dropdowns cuyos valores vienen del CRUD en Ajustes.
 *    Con 1 registro → auto-seleccionado. Con >1 → el más usado en los últimos 7 días.
 *  - Dirección: textarea de ancho completo, obligatoria.
 */
export default function DeliveryNoteBuilder({
  open, onOpenChange, onSubmit, initialInvoiceId = "",
}) {
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesWithConduce, setInvoicesWithConduce] = useState(new Set());
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [customerId, setCustomerId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const [driverId, setDriverId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Cargar catálogos + defaults inteligentes al abrir
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [cs, invs, drvs, vhs, defDrv, defVh, dns] = await Promise.all([
          api.get("/customers"),
          api.get("/invoices"),
          api.get("/settings/fleet/drivers"),
          api.get("/settings/fleet/vehicles"),
          api.get("/settings/fleet/drivers/default"),
          api.get("/settings/fleet/vehicles/default"),
          api.get("/delivery-notes"),
        ]);
        setCustomers(cs.data);
        setInvoices(invs.data);
        // Set de invoice_id que ya tienen un conduce → se ocultan del picker
        // (excepto si es el initialInvoiceId, para no "romper" el flujo si el usuario
        //  hace click en Truck de una factura que ya tiene conduce — mostramos entonces
        //  el error del backend al intentar guardar).
        setInvoicesWithConduce(new Set(
          (dns.data || []).map((d) => d.invoice_id).filter(Boolean),
        ));
        // Sólo activos en los dropdowns
        setDrivers(drvs.data.filter((d) => d.active));
        setVehicles(vhs.data.filter((v) => v.active));
        // Defaults: sólo si el activo por defecto sigue activo
        if (defDrv.data?.id && drvs.data.find((d) => d.id === defDrv.data.id)?.active) {
          setDriverId(defDrv.data.id);
        } else { setDriverId(""); }
        if (defVh.data?.id && vhs.data.find((v) => v.id === defVh.data.id)?.active) {
          setVehicleId(defVh.data.id);
        } else { setVehicleId(""); }
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
    setCustomerId("");
    setInvoiceId(initialInvoiceId || "");
    setInvoiceSearch("");
    setInvoiceOpen(false);
    setDeliveryAddress("");
    setNotes("");
  }, [open, initialInvoiceId]);

  const selectedInvoice = useMemo(
    () => invoices.find((i) => i.id === invoiceId) || null,
    [invoices, invoiceId],
  );
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId) || null,
    [customers, customerId],
  );

  // Si venimos con invoice preseleccionada y aún no hay cliente cargado,
  // sincroniza el cliente desde la factura para el filtro.
  useEffect(() => {
    if (!open || !selectedInvoice) return;
    if (!customerId && selectedInvoice.customer_id) {
      setCustomerId(selectedInvoice.customer_id);
    }
  }, [open, selectedInvoice, customerId]);

  // Facturas filtradas: por cliente si hay uno, por texto de búsqueda, y ocultando
  // las que ya tienen conduce (regla: una factura sólo genera 1 conduce).
  const filteredInvoices = useMemo(() => {
    const s = invoiceSearch.trim().toLowerCase();
    return invoices
      .filter((inv) => {
        if (inv.id !== invoiceId && invoicesWithConduce.has(inv.id)) return false;
        if (customerId && inv.customer_id !== customerId) return false;
        if (!s) return true;
        return (
          (inv.number || "").toLowerCase().includes(s) ||
          (inv.customer_name || "").toLowerCase().includes(s) ||
          (inv.ncf || "").toLowerCase().includes(s)
        );
      })
      .slice(0, 60);
  }, [invoices, invoiceSearch, customerId, invoicesWithConduce, invoiceId]);

  // Cambio de cliente: si la factura actual no corresponde al nuevo cliente, límpiala.
  const handleCustomerChange = (newId) => {
    setCustomerId(newId);
    if (invoiceId) {
      const inv = invoices.find((i) => i.id === invoiceId);
      if (inv && newId && inv.customer_id !== newId) setInvoiceId("");
    }
  };

  const handleInvoicePick = (inv) => {
    setInvoiceId(inv.id);
    setInvoiceOpen(false);
    setInvoiceSearch("");
    // Auto-completar cliente si no hay uno o si es distinto
    if (inv.customer_id && customerId !== inv.customer_id) {
      setCustomerId(inv.customer_id);
    }
  };

  // Items read-only vienen de la factura seleccionada
  const items = selectedInvoice?.items || [];
  const totals = useMemo(() => {
    let subtotal = 0, itbis = 0;
    items.forEach((it) => {
      const line = (it.quantity || 0) * (it.price || 0) - (it.discount || 0);
      subtotal += line;
      if (it.itbis_applies) itbis += line * 0.18;
    });
    return { subtotal, itbis, total: subtotal + itbis };
  }, [items]);

  const submit = async () => {
    if (!invoiceId) return toast.error("Selecciona una factura");
    if (!deliveryAddress.trim()) return toast.error("La dirección de entrega es obligatoria");
    setSaving(true);
    try {
      const payload = {
        invoice_id: invoiceId,
        driver: drivers.find((d) => d.id === driverId)?.name || "",
        vehicle: vehicles.find((v) => v.id === vehicleId)?.name || "",
        delivery_address: deliveryAddress.trim(),
        notes,
      };
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Nuevo conduce
          </DialogTitle>
        </DialogHeader>

        {/* Cliente + Factura */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="overline">Cliente</Label>
            <div className="mt-1 flex gap-1">
              <Select value={customerId} onValueChange={handleCustomerChange}>
                <SelectTrigger data-testid="conduce-customer-select">
                  <SelectValue placeholder="Selecciona (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {customerId && (
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => { setCustomerId(""); }}
                  className="shrink-0"
                  data-testid="conduce-customer-clear"
                  title="Limpiar cliente"
                >
                  ✕
                </Button>
              )}
            </div>
            {selectedCustomer?.rnc_cedula && (
              <div className="text-[10px] text-muted-foreground mt-0.5 data-cell">
                RNC/Ced: {selectedCustomer.rnc_cedula}
              </div>
            )}
          </div>

          <div className="relative">
            <Label className="overline">
              Factura {customerId ? "(de este cliente)" : ""}
            </Label>
            <div className="mt-1 flex gap-1">
              <button
                type="button"
                onClick={() => setInvoiceOpen((o) => !o)}
                className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm text-left flex items-center justify-between hover:border-primary/40 transition-colors"
                data-testid="conduce-invoice-trigger"
              >
                <span className={selectedInvoice ? "" : "text-muted-foreground"}>
                  {selectedInvoice
                    ? `${selectedInvoice.number} · ${formatDOP(selectedInvoice.total || 0)}`
                    : (customerId ? "Elige factura del cliente…" : "Buscar factura (número/cliente/NCF)…")}
                </span>
                <ArrowRight className="w-3 h-3 opacity-50 rotate-90" />
              </button>
              {invoiceId && (
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => setInvoiceId("")}
                  className="shrink-0"
                  data-testid="conduce-invoice-clear"
                  title="Limpiar factura"
                >
                  ✕
                </Button>
              )}
            </div>
            {invoiceOpen && (
              <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-[320px] flex flex-col">
                <div className="p-2 border-b border-border">
                  <Input
                    autoFocus
                    value={invoiceSearch}
                    onChange={(e) => setInvoiceSearch(e.target.value)}
                    placeholder="Buscar (número, cliente, NCF)…"
                    className="h-8 text-sm"
                    data-testid="conduce-invoice-search"
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredInvoices.length === 0 ? (
                    <div className="p-4 text-xs text-center text-muted-foreground">
                      {customerId ? "El cliente no tiene facturas." : "Sin coincidencias."}
                    </div>
                  ) : filteredInvoices.map((inv) => (
                    <button
                      key={inv.id}
                      type="button"
                      onClick={() => handleInvoicePick(inv)}
                      className={`w-full text-left px-3 py-2 hover:bg-accent border-b border-border/40 text-sm ${invoiceId === inv.id ? "bg-primary/10" : ""}`}
                      data-testid={`conduce-invoice-option-${inv.number}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="data-cell font-semibold">{inv.number}</span>
                        <span className="text-xs data-cell text-muted-foreground">{inv.ncf || "—"}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate flex justify-between gap-2">
                        <span>{inv.customer_name || "Cliente Genérico"}</span>
                        <span className="data-cell">{formatDOP(inv.total || 0)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chofer + Vehículo */}
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div>
            <Label className="overline">Chofer</Label>
            {drivers.length === 0 ? (
              <div className="mt-1 h-9 px-3 rounded-md border border-dashed border-input text-xs text-muted-foreground flex items-center">
                Agrega choferes en Ajustes → Choferes y Vehículos
              </div>
            ) : (
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger className="mt-1" data-testid="conduce-driver-select">
                  <SelectValue placeholder="Selecciona chofer" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="overline">Vehículo</Label>
            {vehicles.length === 0 ? (
              <div className="mt-1 h-9 px-3 rounded-md border border-dashed border-input text-xs text-muted-foreground flex items-center">
                Agrega vehículos en Ajustes → Choferes y Vehículos
              </div>
            ) : (
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger className="mt-1" data-testid="conduce-vehicle-select">
                  <SelectValue placeholder="Selecciona vehículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Dirección */}
        <div className="mt-2">
          <Label className="overline">
            Dirección de entrega <span className="text-destructive">*</span>
          </Label>
          <Textarea
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
            placeholder="Escribe la dirección completa: calle, número, sector, ciudad, referencias…"
            className="mt-1 min-h-[80px]"
            data-testid="conduce-address-input"
          />
        </div>

        {/* Notas opcionales */}
        <div className="mt-2">
          <Label className="overline">Notas (opcional)</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observaciones del despacho"
            className="mt-1"
            data-testid="conduce-notes-input"
          />
        </div>

        {/* Items read-only de la factura */}
        <div className="mt-3">
          <Label className="overline">Items del despacho (desde la factura, no editables)</Label>
          <div className="mt-1 border border-border rounded-md max-h-[220px] overflow-y-auto" data-testid="conduce-items-readonly">
            {!selectedInvoice ? (
              <div className="p-4 text-sm text-muted-foreground text-center">
                Selecciona una factura para ver los items del despacho.
              </div>
            ) : items.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">La factura no tiene items.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="overline border-b border-border bg-muted/30">
                  <tr>
                    <th className="text-left p-2">SKU</th>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-right p-2 w-16">Cant.</th>
                    <th className="text-right p-2 w-24">Precio</th>
                    <th className="text-right p-2 w-24">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const line = (it.quantity || 0) * (it.price || 0) - (it.discount || 0);
                    return (
                      <tr key={idx} className="border-b border-border/40">
                        <td className="p-2 data-cell text-xs text-muted-foreground">{it.sku || "—"}</td>
                        <td className="p-2 truncate">{it.name}</td>
                        <td className="p-2 text-right data-cell">{it.quantity}</td>
                        <td className="p-2 text-right data-cell">{formatDOP(it.price)}</td>
                        <td className="p-2 text-right data-cell font-semibold">{formatDOP(line)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {selectedInvoice && (
            <div className="mt-1 text-xs text-right text-muted-foreground">
              Total factura: <span className="data-cell font-bold text-foreground">{formatDOP(totals.total)}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving || !invoiceId || !deliveryAddress.trim()}
            className="bg-primary font-bold"
            data-testid="conduce-save"
          >
            {saving ? "Guardando…" : "Guardar conduce"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
